import { useLocation, Link } from 'react-router-dom';
import {
  House,
  Bell,
  User,
  VideoCamera,
  FilmSlate,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth';
import { NotificationBell } from '@/features/notifications';
import { UserMenu } from './UserMenu';
import { Avatar } from '@vidnight/ui';
import { sound } from '@/utils/sounds';
import { NeonBorder } from './NeonLoadingLine';

/* ─── Tiny badge ──────────────────────────────────────────────── */
function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white"
      style={{ minWidth: 18, height: 18, padding: '0 4px' }}
    >
      {count > 99 ? '99+' : String(count)}
    </span>
  );
}

/* ─── Single nav item (link or button) ────────────────────────── */
function NavItem({
  active,
  badge,
  children,
  ...props
}: {
  active?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
} & (
  | { to: string; onClick?: never }
  | { onClick: () => void; to?: never }
)) {
  const cls = 'flex items-center justify-center h-12 w-14';

  const inner = (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: 32,
        height: 32,
        backgroundColor: active ? 'var(--user-bnav-active-bg)' : undefined,
      }}
    >
      <span style={{ color: active ? 'var(--user-bnav-active-text)' : 'var(--user-bnav-text)' }}>{children}</span>
      {badge}
    </div>
  );

  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={cls} aria-current={active ? 'page' : undefined} onClick={() => sound.whoosh()}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" className={cls} onClick={props.onClick}>
      {inner}
    </button>
  );
}

/* ─── Bottom nav bar ──────────────────────────────────────────── */
export function MobileBottomNav() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  // Hide bottom nav on studio/editor routes — the mobile editor has its own bottom tab bar
  if (location.pathname.startsWith('/studio/')) return null;

  const userName = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
    : '';

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t md:hidden"
      style={{
        backgroundColor: 'var(--user-bnav-bg)',
        borderColor: 'var(--user-bnav-border)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }}
    >
      <NeonBorder />
      <div className="flex justify-evenly">
        {/* Home */}
        <NavItem to="/home" active={location.pathname === '/home'}>
          <House size={28} weight={location.pathname === '/home' ? 'fill' : 'regular'} />
        </NavItem>

        {/* My Media */}
        <NavItem to="/media" active={isActive('/media')}>
          <VideoCamera size={28} weight={isActive('/media') ? 'fill' : 'regular'} />
        </NavItem>

        {/* Studio */}
        <NavItem to="/studio" active={isActive('/studio')}>
          <FilmSlate size={28} weight={isActive('/studio') ? 'fill' : 'regular'} />
        </NavItem>

        {/* Notifications */}
        <NotificationBell
          renderTrigger={({ onClick, unreadCount: bellUnread }) => (
            <NavItem onClick={onClick} badge={<Badge count={bellUnread} />}>
              <Bell size={28} />
            </NavItem>
          )}
        />

        {/* Avatar / User menu */}
        <UserMenu
          renderTrigger={({ onClick }) => (
            <NavItem onClick={onClick}>
              {user ? (
                <Avatar
                  src={user.avatarUrl}
                  name={userName}
                  size="sm"
                  className="!w-9 !h-9 !ring-0 !shadow-none"
                />
              ) : (
                <User size={28} strokeWidth={1.8} />
              )}
            </NavItem>
          )}
        />
      </div>
    </nav>
  );
}
